import { Injectable } from '@angular/core';
import { gql, Query } from 'apollo-angular';
import {
  BasisPrice,
  CashPrice,
  LotsResponse,
  PriceUnion,
} from '../components/bids-and-offers/lots.model';

@Injectable({
  providedIn: 'root',
})
export class LotsService extends Query<LotsResponse> {
  document = gql`
    query Lots($limit: Int, $marketType: Market!) {
      lots(limit: $limit, market_type: $marketType) {
        lot_type
        product_type
        market_type
        commodity
        documentations {
          title
          value
        }
        grade
        indication
        broker_indication
        price {
          ... on CashPrice {
            amount
          }
          ... on BasisPrice {
            month
            year
            product_code
            basisPoints
          }
        }
        currency
        volume_value
        price_units
        units
        ship_begin
        ship_end
        destination
        delivery_type
        time_frame
        additional_info
        rules_to_govern
        weight_grade_to_govern
        payment_term
        origin
        container_type
        delivery_option
        user_id
        _id
        specifications {
          title
          value
        }
      }

      negotiations {
        _id
        lot_id
      }
    }
  `;

  isCashPrice(price: PriceUnion): price is CashPrice {
    if (price) {
      return (price as CashPrice).amount !== undefined;
    }
    return false;
  }

  isBasisPrice(price: PriceUnion): price is BasisPrice {
    if (price) {
      return (price as BasisPrice).month !== undefined;
    }
    return false;
  }

  getPriceText(price: PriceUnion): string {
    if (!price) {
      return null;
    }

    const format = (value: number) => parseFloat(value.toFixed(4)).toString();

    if (this.isCashPrice(price)) {
      return format((price as CashPrice).amount);
    } else if (this.isBasisPrice(price)) {
      const basisPrice = price as BasisPrice;
      const formattedBasis =
        basisPrice.basisPoints < 0
          ? format(basisPrice.basisPoints)
          : '+' + format(basisPrice.basisPoints);

      return `${formattedBasis}${basisPrice.product_code}${basisPrice.month}${
        basisPrice.year % 1000
      }`;
    }
    return null;
  }

  removeProperty<T extends object, K extends keyof T>(
    obj: T,
    prop: K
  ): Omit<T, K> {
    const { [prop]: _, ...newObj } = obj;
    return newObj;
  }
}
